import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosInstance.js';
import TagsInput from './TagsInput'; // Import TagsInput component
import MyUserContext from './../myUserContext.js';
import { Button, Drawer } from "@mantine/core";

const FollowingDropdown = ({ isOpen, onClose, language, art, otherPeopleEvents = [] }) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [recommendationsFromMeMap, setRecommendationsFromMeMap] = useState({});
  const [recommendationsToMeMap, setRecommendationsToMeMap] = useState({});
  const navigate = useNavigate(); // Initialize navigate
  const myUser = useContext(MyUserContext); // Access global myUser context

  // Per-user tag-name lookup, derived from the parent-provided list.
  const tagNamesByEventId = {};
  const userIdsWithEvent = new Set();
  for (const ev of otherPeopleEvents) {
    tagNamesByEventId[ev.artEventId] = ev.eventTagNames || [];
    userIdsWithEvent.add(ev.userId);
  }

  // Surface users who already have an event for this art at the top.
  const sortedUsers = [...users].sort((a, b) => {
    const aHas = userIdsWithEvent.has(a.id) ? 1 : 0;
    const bHas = userIdsWithEvent.has(b.id) ? 1 : 0;
    return bHas - aHas;
  });

  useEffect(() => {
    if (!isOpen) return;
    axiosInstance
      .get('/getUsers?onlyMyFollowersOrFollowees=true')
      .then((response) => setUsers(response.data))
      .catch((error) => console.error('Failed to fetch users:', error));
  }, [isOpen]);

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!myUser?.myUserInfo?.myUserId) return;

      try {
        // Make requests in parallel
        const [fromResponse, toResponse] = await Promise.all([
          axiosInstance.post('/getRecommendations/filters', {
            fromUserId: myUser.myUserInfo.myUserId,
            artId: art.id
          }),
          axiosInstance.post('/getRecommendations/filters', {
            toUserId: myUser.myUserInfo.myUserId,
            artId: art.id
          })
        ]);

        // Process recommendations from me
        const fromMap = {};
        fromResponse.data.forEach((rec) => {
          fromMap[rec.toUser.id] = rec.recommendation.id;
        });
        setRecommendationsFromMeMap(fromMap);

        // Process recommendations to me
        const toMap = {};
        toResponse.data.forEach((rec) => {
          toMap[rec.fromUser.id] = rec.recommendation.id;
        });
        setRecommendationsToMeMap(toMap);
      } catch (error) {
        console.error('Failed to fetch recommendations:', error);
      }
    };

    if (isOpen) {
      fetchRecommendations();
    }
  }, [isOpen, myUser]);

  const toggleUserSelection = (userId) => {
    if (hasEvent(userId)) {
      // If the new user has an event without a "todo" tag, unselect all other users
      if (selectedUsers.length == 1 && selectedUsers[0] === userId) {
        // If the user is already selected, unselect them
        setSelectedUsers([]);
      } else {
        // Unselect all other users
        setSelectedUsers([userId]);
      }
    } else { // userId can be recommended to
      setSelectedUsers((prevSelected) => {
        const userWithEvent = prevSelected.find(
          (userId) =>
            hasEvent(userId) ||
            hasRecommendationFromMe(userId) ||
            hasRecommendationToMe(userId)
        );
        if (userWithEvent) { // there was someone who can't be recommended to
          return [userId];// prevSelected.filter((id) => id !== userWithEventWithoutTodo && id !== userId);
        }
        // Add userId to selectedUsers if not already selected
        return prevSelected.includes(userId)
          ? prevSelected.filter((id) => id !== userId)
          : [...prevSelected, userId];
      });
    }
  };

  const handleRecommend = (toUserIDs) => {
    const updatedMap = { ...recommendationsFromMeMap };

    Promise.all(
      toUserIDs.map((userId) =>
        axiosInstance
          .post('/postRecommendation', { toUserId: userId, artId: art.id })
          .then((response) => {
            updatedMap[userId] = response.data.recommendation.id; // Update map with new recommendation
          })
          .catch((error) => {
            console.error(`Failed to recommend to user ${userId}:`, error);
          })
      )
    ).then(() => {
      setRecommendationsFromMeMap(updatedMap); // Update state after all requests are completed
    });
  };

  const handleClose = () => {
    onClose(); // Close the dropdown when the emoji is clicked
  };

  const hasEvent = (userId, countTodoAsEvent = true) => {
    const eventId = otherPeopleEvents.find(e => e.userId === userId)?.artEventId;
    const tags = tagNamesByEventId[eventId] || [];
    if (countTodoAsEvent) {
      return eventId; // Consider "todo" events as valid events
    } else {
      return eventId && !tags.includes("todo");
    }
  };
  const hasRecommendationFromMe = (userId) => {
    return recommendationsFromMeMap[userId] !== undefined;
  };
  const hasRecommendationToMe = (userId) => {
    return recommendationsToMeMap[userId] !== undefined;
  };

  // const hasEventWithTodoTagOrNoEvent = (userId) => {
  //   const eventId = art?.otherPeopleEvents?.find(e => e.userId === userId)?.artEventId;
  //   const tags = eventTagsMap[eventId]?.map(tag => tag.name) || [];
  //   return !eventId || tags.includes("todo");
  // };

  const navigateToDiscussion = (userId) => {
    const eventId = otherPeopleEvents.find(e => e.userId === userId)?.artEventId;
    navigate(`/artEvents/${eventId}`);
  };

  console.log('recommendationsFromMeMap', recommendationsFromMeMap);
  console.log('recommendationsToMeMap', recommendationsToMeMap);

  return (
    <Drawer
      opened={isOpen}
      onClose={onClose}
      position="bottom"
      size="75vh"
      radius="md"
      padding="md"
      lockScroll={false}
      classNames={{ root: 'art-page-drawer' }}
      title={<span className="font-bold">{language === "ru" ? "Взаимодействие с друзьями" : "Interaction with friends"}</span>}
      styles={{
        header: { paddingBottom: 0 },
        title: { flex: 1, textAlign: 'center' },
        body: { height: 'calc(100% - 50px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
      }}
    >
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-3 gap-4 p-4">
          {(() => {
            // Chunk into rows of 3 so we can inject a full-width action
            // strip right below the row containing the selected user
            // (or the LOWEST row containing any selected user when
            // multiple are selected).
            const rows = [];
            for (let i = 0; i < sortedUsers.length; i += 3) {
              rows.push(sortedUsers.slice(i, i + 3));
            }
            const stripButtons = (() => {
              if (selectedUsers.length === 1) {
                const single = selectedUsers[0];
                return [
                  hasEvent(single) && (
                    <Button
                      key="disc"
                      variant="filled"
                      color="green"
                      size="xs"
                      fullWidth
                      styles={{ root: { height: 39 } }}
                      onClick={() => navigateToDiscussion(single)}
                    >
                      {language === "ru" ? "Перейти к обсуждению" : "Go to discussion"}
                    </Button>
                  ),
                  hasRecommendationFromMe(single) && (
                    <Button
                      key="fromMeRec"
                      variant="filled"
                      color="blue"
                      size="xs"
                      fullWidth
                      styles={{ root: { height: 39 } }}
                      onClick={() => navigate(`/recommendations/${recommendationsFromMeMap[single]}`)}
                    >
                      {language === "ru" ? "Перейти к моей рекомендации" : "Go to my recommendation"}
                    </Button>
                  ),
                  hasRecommendationToMe(single) && (
                    <Button
                      key="toMeRec"
                      variant="filled"
                      color="blue"
                      size="xs"
                      fullWidth
                      styles={{ root: { height: 39 } }}
                      onClick={() => navigate(`/recommendations/${recommendationsToMeMap[single]}`)}
                    >
                      {language === "ru" ? "Перейти к рекомендации от пользователя" : "Go to recommendation from user"}
                    </Button>
                  ),
                  !hasRecommendationFromMe(single) && (
                    <Button
                      key="rec"
                      variant="filled"
                      color="blue"
                      size="xs"
                      fullWidth
                      styles={{ root: { height: 39 } }}
                      onClick={() => handleRecommend([single])}
                    >
                      {language === "ru" ? "Рекомендовать" : "Recommend"}
                    </Button>
                  ),
                ].filter(Boolean);
              }
              if (
                selectedUsers.length > 1 &&
                selectedUsers.every((userId) => !hasRecommendationFromMe(userId))
              ) {
                return [
                  <Button
                    key="recSep"
                    variant="filled"
                    color="blue"
                    size="xs"
                    fullWidth
                    onClick={() => handleRecommend(selectedUsers)}
                  >
                    {language === "ru" ? "Рекомендовать отдельно" : "Recommend separately"}
                  </Button>,
                ];
              }
              return [];
            })();
            let stripRowIdx = -1;
            let stripCol = 0; // 0..2 — which column the single-cell strip sits under
            if (stripButtons.length > 0) {
              if (selectedUsers.length === 1) {
                const uid = selectedUsers[0];
                for (let r = 0; r < rows.length; r++) {
                  const idx = rows[r].findIndex((u) => u.id === uid);
                  if (idx !== -1) { stripRowIdx = r; stripCol = idx; break; }
                }
              } else {
                // Lowest row containing any selected user, aligned under
                // the leftmost selected user in that row.
                for (let r = 0; r < rows.length; r++) {
                  const idx = rows[r].findIndex((u) => selectedUsers.includes(u.id));
                  if (idx !== -1) { stripRowIdx = r; stripCol = idx; }
                }
              }
            }
            const out = [];
            rows.forEach((row, rIdx) => {
              row.forEach((user) => {
                out.push(
                  <div
                    key={user.id}
                    className="text-center cursor-pointer"
                    onClick={() => toggleUserSelection(user.id)}
                  >
                    <div
                      className="w-16 h-16 rounded-full mx-auto border-4"
                      style={{ borderColor: selectedUsers.includes(user.id) ? 'var(--mantine-primary-color-filled)' : 'transparent' }}
                    >
                      <img
                        src={user.avatarUrl}
                        alt={user.userName}
                        className="w-full h-full rounded-full"
                      />
                    </div>
                    <p className="text-sm mt-2">
                      {user.userName.length > 10 ? `${user.userName.slice(0, 10)}...` : user.userName}
                    </p>
                    <div className="flex justify-center items-center mt-1">
                      <TagsInput
                        tags={tagNamesByEventId[otherPeopleEvents.find(e => e.userId === user.id)?.artEventId] || []}
                        editable={false}
                        language={language}
                        reducedView={true}
                      />
                    </div>
                    {recommendationsFromMeMap[user.id] && (
                      <p className="text-xs mt-1" style={{ color: 'var(--mantine-color-blue-text)' }}>
                        {language === "ru" ? "Вы рекомендовали" : "You recommended"}
                      </p>
                    )}
                    {recommendationsToMeMap[user.id] && (
                      <p className="text-xs mt-1" style={{ color: 'var(--mantine-color-green-text)' }}>
                        {language === "ru" ? "Вам рекомендовали" : "Recommended to you"}
                      </p>
                    )}
                  </div>
                );
              });
              if (rIdx === stripRowIdx) {
                out.push(
                  <div
                    key={`action-strip-${rIdx}`}
                    className={`flex flex-col gap-1.5 pt-2 w-[150%] sm:w-full ${stripCol === 2 ? '-ml-[50%] sm:ml-0' : ''}`}
                    style={{ gridColumnStart: stripCol + 1 }}
                  >
                    {stripButtons}
                  </div>
                );
              }
            });
            return out;
          })()}
        </div>
      </div>
    </Drawer>
  );
};

export default FollowingDropdown;
